import { useEffect, useState, useRef } from 'react';
import { getCheckboxDisplayText } from '../../../utils/artDetailsUtils';

// Styles
import styles from './filterOptionsDropdown.module.css';

// Helper function to get docType abbreviation
const getDocTypeAbbr = (docType: string): keyof SelectedFilters => {
  switch (docType) {
    case 'EasyView':
      return 'e';
    case 'LNTransfer':
      return 'l';
    case 'WizardDesign':
    default:
      return 'u';
  }
};

export interface FilterDropdownProps {
  isOpen: boolean;
  selectedFilters: SelectedFilters;
  onApply: (selectedFilters: SelectedFilters) => void;
  onToggle: () => void;
}

export interface SelectedFilters {
  e: string[]; // EasyView
  l: string[]; // LNTransfer
  u: string[]; // WizardDesign
}

export interface SelectedGroups {
  [key: string]: string[]; // groupKey: [products in that group]
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  isOpen,
  selectedFilters,
  onApply,
  onToggle
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [tempSelectedFilters, setTempSelectedFilters] = useState<SelectedFilters>({
    e: [...(selectedFilters.e || [])],
    l: [...(selectedFilters.l || [])],
    u: [...(selectedFilters.u || [])],
  });
  const [_, setTempSelectedGroups] = useState<SelectedGroups>({});

  const dropdownRef = useRef<HTMLDivElement>(null);

  const hasData = Object.values(tempSelectedFilters).some(
    (filter) => Array.isArray(filter) && filter.length > 0
  );

  const hasAppliedFilters = Object.values(selectedFilters).some(
    (filter) => Array.isArray(filter) && filter.length > 0
  );

  // Calculate applied groups based on APPLIED filters (selectedFilters, not temp)
  const getAppliedGroups = (): SelectedGroups => {
    const appliedGroups: SelectedGroups = {};

    dropdownSections.forEach(section => {
      const docTypeAbbr = getDocTypeAbbr(section.docType);
      const currentFilters = selectedFilters[docTypeAbbr] || [];

      Object.entries(section.productType).forEach(([groupKey, products]) => {
        const allProductsSelected = products.every((product: string) =>
          currentFilters.includes(product)
        );

        if (allProductsSelected && products.length > 0) {
          const uniqueGroupKey = `${section.docType}-${groupKey}`;
          appliedGroups[uniqueGroupKey] = products;
        }
      });
    });

    return appliedGroups;
  };

  // Get total count of applied filters (groups + individual products)
  const appliedFiltersCount = () => {
    const appliedGroups = getAppliedGroups();
    const groupCount = Object.keys(appliedGroups).length;
    const individualProductCount = Object.values(selectedFilters).reduce((total, filterArray) => {
      if (!Array.isArray(filterArray)) return total;

      const productsNotInGroups = filterArray.filter(product => {
        return !Object.values(appliedGroups).some(groupProducts =>
          groupProducts.includes(product)
        );
      });

      return total + productsNotInGroups.length;
    }, 0);

    return groupCount + individualProductCount;
  };

  // Get all applied filter items for display as pills (based on APPLIED filters only)
  const getAppliedFilterItems = () => {
    const items: Array<{ type: 'group' | 'product', key: string, displayText: string }> = [];
    const appliedGroups = getAppliedGroups();

    Object.keys(appliedGroups).forEach(uniqueGroupKey => {
      const groupKey = uniqueGroupKey.split('-').slice(1).join('-');

      items.push({
        type: 'group',
        key: uniqueGroupKey,
        displayText: groupKey === 'UltraColorDTFTransfers'
          ? `${getText[groupKey as keyof typeof getText] || groupKey} - ${getText[uniqueGroupKey.split('-')[0] as keyof typeof getText]
          ?? getText[appliedGroups[uniqueGroupKey][0] as keyof typeof getText]}`
          : getText[groupKey as keyof typeof getText] || groupKey
      });
    });

    Object.values(selectedFilters).forEach(filterArray => {
      if (Array.isArray(filterArray)) {
        filterArray.forEach(product => {
          const isInAppliedGroup = Object.values(appliedGroups).some(groupProducts =>
            groupProducts.includes(product)
          );

          if (!isInAppliedGroup) {
            items.push({
              type: 'product',
              key: product,
              displayText: getCheckboxDisplayText(product)
            });
          }
        });
      }
    });

    return items;
  };

  const removeFilter = (itemKey: string, itemType: 'group' | 'product') => {
    if (itemType === 'group') {
      // Remove group and its products
      const appliedGroups = getAppliedGroups();
      const groupProducts = appliedGroups[itemKey] || [];
      const newFilters = { ...selectedFilters };

      Object.keys(newFilters).forEach(key => {
        const filterKey = key as keyof SelectedFilters;
        newFilters[filterKey] = newFilters[filterKey].filter(item =>
          !groupProducts.includes(item)
        );
      });

      onApply(newFilters);
    } else {
      const newFilters = { ...selectedFilters };

      Object.keys(newFilters).forEach(key => {
        const filterKey = key as keyof SelectedFilters;
        const index = newFilters[filterKey].indexOf(itemKey);
        if (index !== -1) {
          newFilters[filterKey] = newFilters[filterKey].filter(item => item !== itemKey);
        }
      });

      onApply(newFilters);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        if (isOpen) {
          onToggle();
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onToggle]);

  useEffect(() => {
    if (isOpen) {
      setTempSelectedFilters({
        e: [...(selectedFilters.e || [])],
        l: [...(selectedFilters.l || [])],
        u: [...(selectedFilters.u || [])],
      });
      setTempSelectedGroups(getAppliedGroups());

      // Auto-expand sections and groups that have applied filters
      const newExpandedSections: Record<string, boolean> = {};
      const newExpandedGroups: Record<string, boolean> = {};

      dropdownSections.forEach(section => {
        const docTypeAbbr = getDocTypeAbbr(section.docType);
        const sectionHasFilters = selectedFilters[docTypeAbbr]?.length > 0;

        if (sectionHasFilters) {
          newExpandedSections[section.docType] = true;

          Object.entries(section.productType).forEach(([groupKey, products]) => {
            const groupHasFilters = products.some((product: string) =>
              selectedFilters[docTypeAbbr]?.includes(product)
            );

            if (groupHasFilters) {
              newExpandedGroups[`${section.docType}-${groupKey}`] = true;
            }
          });
        }
      });

      setExpandedSections(newExpandedSections);
      setExpandedGroups(newExpandedGroups);
    }
  }, [isOpen, selectedFilters]);

  const toggleSection = (docType: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [docType]: !prev[docType]
    }));
  };

  const toggleGroup = (docType: string, groupKey: string) => {
    const key = `${docType}-${groupKey}`;
    setExpandedGroups(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleGroupCheckboxChange = (docType: string, products: string[]) => {
    const docTypeAbbr = getDocTypeAbbr(docType);
    const currentFilters = tempSelectedFilters[docTypeAbbr] || [];

    // Check if all products in this group are currently selected
    const allProductsSelected = products.every(product => currentFilters.includes(product));

    if (allProductsSelected) {
      // Remove group - remove all its products from filters
      setTempSelectedFilters(prev => ({
        ...prev,
        [docTypeAbbr]: prev[docTypeAbbr].filter(item => !products.includes(item))
      }));
    } else {
      // Add group - add all its products to filters
      setTempSelectedFilters(prev => {
        const currentArray = [...prev[docTypeAbbr]];
        const productsToAdd = products.filter(product => !currentArray.includes(product));

        return {
          ...prev,
          [docTypeAbbr]: [...currentArray, ...productsToAdd]
        };
      });
    }
  };

  const handleCheckboxChange = (productKey: string, docType: string) => {
    const docTypeAbbr = getDocTypeAbbr(docType);

    setTempSelectedFilters(prev => {
      const currentArray = [...prev[docTypeAbbr]];
      const index = currentArray.indexOf(productKey);

      if (index === -1) {
        // Add the product
        return {
          ...prev,
          [docTypeAbbr]: [...currentArray, productKey],
        };
      } else {
        // Remove the product
        currentArray.splice(index, 1);
        return {
          ...prev,
          [docTypeAbbr]: currentArray,
        };
      }
    });
  };

  const handleApply = () => {
    onApply(tempSelectedFilters);
    onToggle();
  };

  const isProductSelected = (productKey: string, docType: string) => {
    const docTypeAbbr = getDocTypeAbbr(docType);
    return tempSelectedFilters[docTypeAbbr]?.includes(productKey) || false;
  };

  const isGroupSelected = (groupKey: string, docType: string) => {
    const docTypeAbbr = getDocTypeAbbr(docType);
    const currentFilters = tempSelectedFilters[docTypeAbbr] || [];
    const section = dropdownSections.find(s => s.docType === docType);
    if (!section) return false;

    //@ts-ignore
    const products = section.productType[groupKey];
    if (!products || products.length === 0) return false;

    return products.every((product: string) => currentFilters.includes(product));
  };

  if (!isOpen) {
    return (
      <div className={styles.filterDropdown}>
        <div className="relative">
          <button
            onClick={onToggle}
            className="inline-flex justify-center items-center bg-[white] transition-all duration-[0.15s] ease-[ease-in-out] font-medium uppercase text-[#F36E29] px-[1.25em] py-[0.4em] rounded-[22px] border-2 border-solid border-[#F36E29]">
            <span className="pr-2">Filter</span>
            <FilterIcon />
          </button>
          {hasAppliedFilters && (
            <div className="absolute -top-2 -right-2 bg-[#F36E29] text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center min-w-[24px]">
              {appliedFiltersCount()}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.filterDropdown} ref={dropdownRef}>
      <button
        onClick={onToggle}
        className="inline-flex justify-center items-center bg-[white] transition-all duration-[0.15s] ease-[ease-in-out] font-medium uppercase text-[#F36E29] px-[1.25em] py-[0.4em] rounded-[22px] border-2 border-solid border-[#F36E29]">
        <span className="pr-2">Filter</span>
        <FilterIcon />
      </button>

      <div className={styles.dropdownContent}>
        <div className={`${styles.sectionContainer} max-h-[680px] overflow-y-auto`}>
          {hasAppliedFilters &&
            <div className='w-full flex flex-col p-6 border-b-slate-200 border-b border-solid gap-4'>
              <span className='font-semibold text-base'>Selected</span>
              <div className='flex flex-wrap gap-[12px_8px]'>
                {getAppliedFilterItems().map((item) => (
                  <div
                    key={`${item.type}-${item.key}`}
                    className='inline-flex items-center bg-white border border-black rounded-full px-3 py-1 text-sm'
                  >
                    <span className='mr-2'>
                      {item.displayText}
                    </span>
                    <button
                      onClick={() => removeFilter(item.key, item.type)}
                      className='flex items-center justify-center w-4 h-4 rounded-full hover:bg-gray-100 transition-colors'
                      aria-label={`Remove ${item.displayText}`}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        className='stroke-current'
                      >
                        <path
                          d="M9 3L3 9M3 3L9 9"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          }
          {dropdownSections.map((section) => (
            <div key={section.docType} className='border-b-slate-200 border-b border-solid'>

              {/* Doc Types (sections) */}
              <div
                className={`${styles.categoryHeader} border-l-[5px] border-l-[#F36E29] border-solid py-4 px-4`}
                onClick={() => toggleSection(section.docType)}
              >
                <span className='font-semibold text-base'>{section.label}</span>
                <span className={styles.icon}>
                  {expandedSections[section.docType] ? <DownIcon /> : <NextIcon />}
                </span>
              </div>

              {/* Groups */}
              {expandedSections[section.docType] && (
                <div className={styles.expandedSection}>
                  {Object.entries(section.productType).map(([groupKey, products]) => {
                    return <div key={groupKey}>
                      {/* Group Header */}
                      {!section.productType['LNProductType']?.length && <div
                        className={`${styles.categoryHeader} pl-[2.5em] pr-4 py-2 border-b-slate-200 border-b border-solid flex items-center`}
                      >
                        <div className="flex items-center flex-1">
                          <label
                            htmlFor={`group-${section.docType}-${groupKey}`}
                            className={`${styles.otCheckbox} mr-3 cursor-pointer mb-[22px]`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              id={`group-${section.docType}-${groupKey}`}
                              checked={isGroupSelected(groupKey, section.docType)}
                              onChange={() => handleGroupCheckboxChange(section.docType, products)}
                            />
                            <span className={styles.checkmark}></span>
                          </label>
                          <span
                            style={{ fontWeight: '500', fontSize: '14px', cursor: 'pointer', flex: 1 }}
                            onClick={() => toggleGroup(section.docType, groupKey)}
                          >
                            {getText[groupKey as keyof typeof getText] || groupKey}
                          </span>
                        </div>
                        <span
                          className={`${styles.icon} cursor-pointer`}
                          onClick={() => toggleGroup(section.docType, groupKey)}
                        >
                          {expandedGroups[`${section.docType}-${groupKey}`] ? <DownIcon /> : <NextIcon />}
                        </span>
                      </div>
                      }

                      {/* Products - Checkboxes */}
                      {(expandedGroups[`${section.docType}-${groupKey}`] || section.productType['LNProductType']?.length) && (
                        <div className={styles.categoryItems}>
                          {products.map((productKey: string) => (
                            <div key={productKey} className={styles.optionsWrapper}>
                              <label
                                htmlFor={`${section.docType}-${groupKey}-${productKey}`}
                                className={styles.otCheckbox}>
                                <input
                                  type="checkbox"
                                  id={`${section.docType}-${groupKey}-${productKey}`}
                                  checked={isProductSelected(productKey, section.docType)}
                                  onChange={() => handleCheckboxChange(productKey, section.docType)}
                                />
                                <span className={styles.checkmark}></span>
                                {getCheckboxDisplayText(productKey)}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className={styles.dropdownFooter}>
          <button onClick={handleApply}
            disabled={!hasData}
            className={[styles.acceptBtn, !hasData ? styles.disabled : ''].join(' ')}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterDropdown;

// Mapping
const dropdownSections = [
  {
    docType: 'WizardDesign',
    label: 'Wizard Design Service Types/Product Types',
    productType: {
      "CadPrintz": [
        "BacklitGraphics",
        "Banners",
        "CanvasPrints",
        "DigitalTransfersLogos",
        "DryEraseGraphics",
        "FloorGraphics",
        "HelmetStickers",
        "Posters",
        "StaticClings",
        "StickersDecals",
        "WallGraphics",
      ],
      "UltraColorDTFTransfers": [
        "DTFTransfer",
      ],
      "CustomCut": [
        "CustomCadCut",
        "CustomCutSignVinyl",
        "CustomKissCut",
        "CustomPreAlignedPTW",
        "CustomSimStitch",
        "CustomCut",
      ],
      "CustomCutLetters": [
        "CustomCutPreAlignedPTWLt",
        "SimStitchLetters",
        "CustomCutLetters",
      ],
      "CustomCutNumbers": [
        "CustomCutPreAlignedPTWNo",
        "SimStitchNumbers",
        "CustomCutNumbers",
      ],
      "3DEmbrPatches": [
        "3DEmbrPatchesHA",
        "3DEmbrPatchesPS",
      ],
      "EmbrPatches": [
        "EmbrPatchesHA",
        "EmbrPatchesHL",
        "EmbrPatchesPS",
        "PrintStitchPatchesHA",
        "PrintStitchPatchesHL",
        "PrintStitchPatchesPS",
      ],
      "Flexbroidery": [
        "FlexbroideryHA",
        "FlexbroideryPS",
      ],
      "FlexStyle": [
        "BeveledEmblemsHA",
        "BeveledEmblemsPS",
        "DomeXEmblemsHA",
        "DomeXEmblemsPS",
        "FlatDomeXEmblemsHA",
        "FlatDomeXEmblemsHL",
        "FlatDomeXEmblemsPS",
        "DecoXEmblemsHA",
        "DecoXEmblemsPS",
      ],
      "ChenillePatches": [
        "ChenillePatchesHA",
        "ChenillePatchesPS",
      ],
      "LeatherPatches": [
        "LeatherPatchesHA",
        "LeatherPatchesPS",
        "FauxLeatherPatchesHA",
        "FauxLeatherPatchesPS",
        "FauxSuedePatchesHA",
        "FauxSuedePatchesPS",
      ],
      "FlexStyleHolographic": [
        "FlexStyleHolographicHA",
        "FlexStyleHolographicPS",
      ],
      "PVCEmblems": [
        "PVPlusPatchHA",
        "PVPlusPatchPS",
      ],
      "WovenPatches": [
        "WovenPatchHA",
        "WovenPatchPS",
      ],
      "DebossedLP": [
        "DebossedLeatherPatchesHA",
        "DebossedLeatherPatchesPS",
      ],
      "CustomPatches": [
        "CustomPatchesEmbroidered",
        "CustomPatchesVintage",
      ],
      "CustomProduct": [
        "CustomProduct",
      ],
    }
  },
  {
    docType: 'EasyView',
    label: 'Easy View Service Types/Product Types',
    productType: {
      "UltraColorDTFTransfers": ["EasyView"]
    }
  },
  {
    docType: 'LNTransfer',
    label: 'L&N Product Types',
    productType: {
      "LNProductType": ['CustomMadeEasyCadCut', 'CustomMadeEasyKissCut', 'CustomMadeEasySimStitch', 'CustomMadeEasyStandard', 'CustomMadeEasySublimatedSimStitch', 'CustomMadeEasySublimatedTwill']
    }
  }
]

export const getText = {
  "3DEmbrPatches": "3D Embroidered Patches",
  "3DEmbrPatchesHA": "3D Embroidered Patches Heat Applied",
  "3DEmbrPatchesPS": "3D Embroidered Patches Pressure Sensitive/Sticker",
  "CadPrintz": "CAD-PRINTZ® Digital Transfers",
  "BacklitGraphics": "Backlit Graphics",
  "Banners": "Banners",
  "CanvasPrints": "Canvas Prints",
  "DigitalTransfersLogos": "Digital Transfers/Logos",
  "DryEraseGraphics": "Dry Erase Graphics",
  "FloorGraphics": "Floor Graphics",
  "HelmetStickers": "Helmet Stickers",
  "Posters": "Posters",
  "StaticClings": "Static Clings",
  "StickersDecals": "Stickers/Decals",
  "WallGraphics": "Wall Graphics",
  "FlexStyle": "FlexStyle® Emblems",
  "BeveledEmblemsHA": "Beveled Emblem Heat Applied",
  "BeveledEmblemsPS": "Beveled Emblem Pressure Sensitive/Sticker",
  "DomeXEmblemsHA": "Domed Emblem Heat Applied",
  "DomeXEmblemsPS": "Domed Emblem Pressure Sensitive/Sticker",
  "FlatDomeXEmblemsHA": "Flat Emblem Heat Applied",
  "FlatDomeXEmblemsHL": "Flat Emblem Hook & Loop",
  "FlatDomeXEmblemsPS": "Flat Emblem Pressure Sensitive/Sticker",
  "DecoXEmblemsHA": "Textured Emblem Heat Applied",
  "DecoXEmblemsPS": "Textured Emblem Pressure Sensitive/Sticker",
  "CustomCut": "Custom Cut Designs",
  "CustomCadCut": "CAD-CUT® Designs",
  "CustomCutSignVinyl": "Custom Cut Sign Vinyl",
  "CustomKissCut": "Kiss Cut® Designs",
  "CustomPreAlignedPTW": "Pre-Aligned Perma-Twill® Designs",
  "CustomSimStitch": "SimStitch® Designs",
  "CustomCutStandard": "Standard Designs", // CustomCut child value
  "ChenillePatches": "Chenille Patches",
  "ChenillePatchesHA": "Chenille Patches Heat Applied",
  "ChenillePatchesPS": "Chenille Patches Pressure Sensitive/Sticker",
  "EmbrPatches": "Embroidered & Print Stitch Patches",
  "EmbrPatchesHA": "Embroidered Patches Heat Applied",
  "EmbrPatchesHL": "Embroidered Patches Hook & Loop",
  "EmbrPatchesPS": "Embroidered Patches Pressure Sensitive/Sticker",
  "PrintStitchPatchesHA": "Print Stitch Patches Heat Applied",
  "PrintStitchPatchesHL": "Print Stitch Patches Hook & Loop",
  "PrintStitchPatchesPS": "Print Stitch Patches Pressure Sensitive/Sticker",
  "LeatherPatches": "Etched Leather & Suede Patches (Genuine & Faux)",
  "LeatherPatchesHA": "Leather Patches Heat Applied",
  "LeatherPatchesPS": "Leather Patches Pressure Sensitive/Sticker",
  "FauxLeatherPatchesHA": "Faux Leather Patches Heat Applied",
  "FauxLeatherPatchesPS": "Faux Leather Patches Pressure Sensitive/Sticker",
  "FauxSuedePatchesHA": "Faux Suede Patches Heat Applied",
  "FauxSuedePatchesPS": "Faux Suede Patches Pressure Sensitive/Sticker",
  "Flexbroidery": "Flexbroidery",
  "FlexbroideryHA": "Flexbroidery Heat Applied",
  "FlexbroideryPS": "Flexbroidery Pressure Sensitive/Sticker",
  "FlexStyleHolographic": "FlexStyle® Holographic Emblems",
  "FlexStyleHolographicHA": "Holographic Emblem Heat Applied",
  "FlexStyleHolographicPS": "Holographic Emblem Pressure Sensitive/Sticker",
  "CustomCutLetters": "Custom Cut Letters",
  "CustomCutPreAlignedPTWLt": "Perma-Twill® Letters",
  "SimStitchLetters": "SimStitch® Letters",
  "CustomCutLettersStandard": "Standard Letters", //CustomCutLetters child value
  "CustomCutNumbers": "Custom Cut Numbers",
  "CustomCutPreAlignedPTWNo": "Perma-Twill® Numbers",
  "SimStitchNumbers": "SimStitch® Numbers",
  "CustomCutNumbersStandard": "Standard Numbers", //CustomCutNumbers child value
  "PVCEmblems": "PVC Patch",
  "PVPlusPatchHA": "PVC Patch Heat Applied",
  "PVPlusPatchPS": "PVC Patch Pressure Sensitive/Sticker",
  "UltraColorDTFTransfers": "UltraColor® MAX DTF Transfers",
  "DTFTransfer": "UltraColor® MAX",
  "WovenPatches": "Woven Patches",
  "WovenPatchHA": "Woven Patch Heat Applied",
  "WovenPatchPS": "Woven Patch Pressure Sensitive/Sticker",
  "CustomPatches": "Custom Patches",
  "CustomPatchesEmbroidered": "Embroidered",
  "CustomPatchesVintage": "Vintage",
  "CustomProduct": "Custom Product",
  "EasyView": "Easy View",
  "CustomMadeEasyCadCut": "AWAW CAD-CUT",
  "CustomMadeEasyKissCut": "AWAW Kiss Cut",
  "CustomMadeEasySimStitch": "AWAW SimStitch",
  "CustomMadeEasyStandard": "AWAW Standard",
  "CustomMadeEasySublimatedSimStitch": "AWAW Sublimated SimStitch",
  "CustomMadeEasySublimatedTwill": "AWAW Sublimated Twill",
  "LNProductType": "L&N Product Types",
  "EmblemWizard": "Emblem Wizard",
  "DebossedLP": "Debossed Genuine Leather Patches",
  "DebossedLeatherPatchesHA": "Debossed Leather Patch Heat Applied",
  "DebossedLeatherPatchesPS": "Debossed Leather Patch Press Sensitive/Sticker",
}

// Icons
function NextIcon() {
  return (
    <svg
      height="24px"
      width="24px"
      viewBox="0 0 24 24"
      fill="none">
      <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"></path></svg>
  );
}

function DownIcon() {
  return (
    <svg fill='none'
      viewBox="0 0 24 24"
      height="20px"
      width="20px" strokeWidth={2.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

export const FilterIcon = () => {
  return <svg
    fill='none'
    viewBox="0 0 24 24"
    height="18px"
    width="18px" strokeWidth={1.5} stroke="currentColor">
    <path d="M3 4.6C3 4.03995 3 3.75992 3.10899 3.54601C3.20487 3.35785 3.35785 3.20487 3.54601 3.10899C3.75992 3 4.03995 3 4.6 3H19.4C19.9601 3 20.2401 3 20.454 3.10899C20.6422 3.20487 20.7951 3.35785 20.891 3.54601C21 3.75992 21 4.03995 21 4.6V6.33726C21 6.58185 21 6.70414 20.9724 6.81923C20.9479 6.92127 20.9075 7.01881 20.8526 7.10828C20.7908 7.2092 20.7043 7.29568 20.5314 7.46863L14.4686 13.5314C14.2957 13.7043 14.2092 13.7908 14.1474 13.8917C14.0925 13.9812 14.0521 14.0787 14.0276 14.1808C14 14.2959 14 14.4182 14 14.6627V17L10 21V14.6627C10 14.4182 10 14.2959 9.97237 14.1808C9.94787 14.0787 9.90747 13.9812 9.85264 13.8917C9.7908 13.7908 9.70432 13.7043 9.53137 13.5314L3.46863 7.46863C3.29568 7.29568 3.2092 7.2092 3.14736 7.10828C3.09253 7.01881 3.05213 6.92127 3.02763 6.81923C3 6.70414 3 6.58185 3 6.33726V4.6Z"
      stroke="#F36E29" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    </path>
  </svg>
}